import { useNavigate } from "react-router";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGetApiAutomationInbox } from "@/lib/api/generated/automations/automations";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { ROUTES } from "@/app/routes";

function formatRelative(value: string) {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return "";
  }

  const deltaMinutes = Math.max(1, Math.round((Date.now() - timestamp) / 60_000));
  if (deltaMinutes < 60) {
    return `${deltaMinutes}m ago`;
  }

  const deltaHours = Math.round(deltaMinutes / 60);
  if (deltaHours < 24) {
    return `${deltaHours}h ago`;
  }

  const deltaDays = Math.round(deltaHours / 24);
  return `${deltaDays}d ago`;
}

export function AutomationInboxMenu() {
  const navigate = useNavigate();
  const { tenantId } = useTenantContext();
  const inboxQuery = useGetApiAutomationInbox(
    { limit: 5 },
    { query: { enabled: Boolean(tenantId), refetchInterval: 15_000 } },
  );

  if (!tenantId) {
    return null;
  }

  const unreadCount = inboxQuery.data?.unreadCount ?? 0;
  const notifications = inboxQuery.data?.notifications ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-8">
          <Bell className="size-4" />
          {unreadCount > 0 ? (
            <span className="absolute -right-0.5 -top-0.5">
              <Badge variant="error" className="min-w-4 justify-center px-1 py-0 text-[10px]">
                {unreadCount > 9 ? "9+" : unreadCount}
              </Badge>
            </span>
          ) : null}
          <span className="sr-only">Open automation inbox</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px]">
        <DropdownMenuLabel className="flex items-center justify-between gap-3">
          <span>Automation Inbox</span>
          {unreadCount > 0 ? <Badge variant="outline">{unreadCount} unread</Badge> : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="px-2 py-6 text-center text-xs" style={{ color: "var(--muted-foreground)" }}>
            No inbox deliveries yet.
          </div>
        ) : (
          notifications.map((notification) => (
            <DropdownMenuItem
              key={notification.notificationId}
              className="items-start"
              onClick={() => navigate(ROUTES.tenantAutomationInboxDetail(tenantId, notification.notificationId))}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{notification.automationName}</span>
                  <span className="shrink-0 text-[10px]" style={{ color: "var(--muted-foreground)" }}>
                    {formatRelative(notification.createdAt)}
                  </span>
                </div>
                <span className="line-clamp-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                  {notification.summary}
                </span>
              </div>
              {notification.status === "unread" ? (
                <span className="mt-1 size-2 rounded-full" style={{ background: "var(--ok)" }} />
              ) : null}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(ROUTES.tenantAutomationInbox(tenantId))}>
          View all inbox items
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
